import React from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface InvoiceRow {
  'Document Number'?: string;
  Date?: string;
  'Due Date'?: string;
  'Current Amount'?: string;
  Overdue?: string;
}

interface ConfigStatus {
  ready: boolean;
  missing: string[];
  threshold?: number;
  thresholdFormatted: string;
}

interface MonitorResult {
  checkedAt?: string;
  thresholdFormatted?: string;
  totalOutstandingFormatted?: string;
  invoiceCount?: number;
  overdueCount?: number;
  isOverThreshold?: boolean;
  alertSent?: boolean;
  openInvoices?: InvoiceRow[];
  largestInvoices?: InvoiceRow[];
}

interface StatusResponse {
  success: boolean;
  config: ConfigStatus;
  state: {
    last_status?: string;
    last_error?: string;
    last_checked_at?: string;
    last_alert_at?: string;
  } | null;
  lastResult: MonitorResult | null;
}

interface CheckJob {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: number;
  progress_message?: string;
  result?: MonitorResult;
  error_message?: string;
}

const formatDateTime = (value?: string) => {
  if (!value) return 'Not checked yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const LSSInvoices: React.FC = () => {
  const [status, setStatus] = React.useState<StatusResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [checking, setChecking] = React.useState(false);
  const [savingThreshold, setSavingThreshold] = React.useState(false);
  const [thresholdInput, setThresholdInput] = React.useState('35000');
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const pollTimeoutRef = React.useRef<number | null>(null);

  const loadStatus = React.useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get<StatusResponse>('/api/lss-invoices/status');
      setStatus(response.data);
      setThresholdInput(String(response.data.config?.threshold ?? 35000));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not load the invoice monitor status.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadStatus();
    return () => {
      if (pollTimeoutRef.current) {
        window.clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [loadStatus]);

  const pollCheckJob = React.useCallback(async (jobId: string) => {
    try {
      const response = await axios.get<{ success: boolean; job: CheckJob }>(`/api/lss-invoices/jobs/${jobId}/status`);
      const job = response.data.job;

      if (job.status === 'completed') {
        setSuccessMessage(
          job.result?.alertSent
            ? 'Invoice check finished and an Asana alert was created.'
            : 'Invoice check finished successfully.'
        );
        setChecking(false);
        await loadStatus();
        return;
      }

      if (job.status === 'failed') {
        setError(job.error_message || 'The invoice check failed. Please try again.');
        setChecking(false);
        await loadStatus();
        return;
      }

      setSuccessMessage(job.progress_message || 'Invoice check is still running.');
      pollTimeoutRef.current = window.setTimeout(() => pollCheckJob(jobId), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not check the invoice job status. Please refresh and try again.');
      setChecking(false);
    }
  }, [loadStatus]);

  const runCheck = async () => {
    try {
      setChecking(true);
      setError(null);
      setSuccessMessage(null);
      const response = await axios.post('/api/lss-invoices/check', { createAlert: true });
      setSuccessMessage('Invoice check started. Waiting for LSS and the verification email relay.');
      await pollCheckJob(response.data.jobId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'The invoice check failed. Please try again.');
      if (err.response?.data?.config) {
        setStatus((current) => ({
          success: false,
          config: err.response.data.config,
          state: current?.state || null,
          lastResult: current?.lastResult || null,
        }));
      }
    } finally {
      setChecking(false);
    }
  };

  const saveThreshold = async () => {
    try {
      setSavingThreshold(true);
      setError(null);
      setSuccessMessage(null);
      const response = await axios.patch<StatusResponse>('/api/lss-invoices/settings', {
        threshold: thresholdInput,
      });
      setStatus(response.data);
      setThresholdInput(String(response.data.config?.threshold ?? thresholdInput));
      setSuccessMessage('Invoice alert threshold saved.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not save the threshold. Please check the amount and try again.');
    } finally {
      setSavingThreshold(false);
    }
  };

  const config = status?.config;
  const result = status?.lastResult;
  const lastState = status?.state;
  const overThreshold = Boolean(result?.isOverThreshold);
  const openInvoices = result?.openInvoices || result?.largestInvoices || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <ReceiptLongIcon color="primary" />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                LSS Invoices
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary">
              Monitor outstanding LSS invoices and trigger the admin Asana alert when the total crosses the threshold.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={checking ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={runCheck}
            disabled={checking || loading}
            sx={{ minWidth: 150 }}
          >
            {checking ? 'Checking' : 'Check now'}
          </Button>
        </Stack>
      </Paper>

      {loading && (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={240}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <Stack spacing={3}>
          {error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={loadStatus}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {config && !config.ready && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              Missing required Heroku configuration: {config.missing.join(', ')}.
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Total outstanding
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {result?.totalOutstandingFormatted || '$0.00'}
                  </Typography>
                  <Chip
                    size="small"
                    color={overThreshold ? 'warning' : 'success'}
                    label={overThreshold ? 'Above threshold' : 'Below threshold'}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Alert threshold
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {config?.thresholdFormatted || '$35,000.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {config?.ready ? 'Configuration ready' : 'Configuration incomplete'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Open invoices
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {result?.invoiceCount ?? '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {result?.overdueCount ?? 0} overdue
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Last checked
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatDateTime(result?.checkedAt || lastState?.last_checked_at)}
                  </Typography>
                  <Typography variant="body2" color={lastState?.last_status === 'error' ? 'error' : 'text.secondary'} sx={{ mt: 1 }}>
                    {lastState?.last_status === 'error' ? lastState?.last_error : 'Latest status saved'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Alert Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set the outstanding invoice total that should create an Asana alert.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  label="Threshold"
                  value={thresholdInput}
                  onChange={(event) => setThresholdInput(event.target.value)}
                  size="small"
                  type="number"
                  inputProps={{ min: 1, step: '0.01' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  disabled={savingThreshold}
                />
                <Button
                  variant="contained"
                  startIcon={savingThreshold ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={saveThreshold}
                  disabled={savingThreshold || loading}
                  sx={{ minWidth: 120 }}
                >
                  {savingThreshold ? 'Saving' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Open Invoices by Due Date
              </Typography>
              {lastState?.last_alert_at && (
                <Chip size="small" color="warning" label={`Last alert ${formatDateTime(lastState.last_alert_at)}`} />
              )}
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Document #</TableCell>
                    <TableCell>Invoice Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Overdue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {openInvoices.length > 0 ? (
                    openInvoices.map((invoice) => (
                      <TableRow key={invoice['Document Number'] || `${invoice.Date}-${invoice['Current Amount']}`}>
                        <TableCell>{invoice['Document Number'] || '-'}</TableCell>
                        <TableCell>{invoice.Date || '-'}</TableCell>
                        <TableCell>{invoice['Due Date'] || '-'}</TableCell>
                        <TableCell align="right">{invoice['Current Amount'] || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={invoice.Overdue === 'Yes' ? 'error' : 'default'}
                            label={invoice.Overdue === 'Yes' ? 'Yes' : 'No'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No invoice check results are available yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}
    </Container>
  );
};

export default LSSInvoices;
